"use client";
import { TypeBannerSkeleton } from "@contentful/types";
import { Banner } from "@yardzen/ui/components/banner";
import { Entry } from "contentful";
import Cookies from "js-cookie";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

export function AnnouncementBanner({
  contentfulBanner,
}: {
  contentfulBanner: Entry<
    TypeBannerSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  > | null;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isMounted, setIsMounted] = useState(false);

  const [discountCodeCookie, setDiscountCodeCookie] = useState<
    string | undefined
  >(undefined);
  const [hideDiscountCodeBanner, setHideDiscountCodeBanner] = useState<
    string | undefined
  >(undefined);
  const [hideAnnouncementBanner, setHideAnnouncementBanner] = useState<
    string | undefined
  >(undefined);
  const [currentBannerVariant, setCurrentBannerVariant] = useState<
    "promo" | "announcement" | "hidden"
  >("hidden");

  const announcementBanner = contentfulBanner;
  const productionToggle = announcementBanner?.fields.active;
  const stagingToggle = searchParams.get("banner") === "true";
  const announcementToggle = productionToggle || stagingToggle;
  // A promo code in the URL (e.g. a builder-partner link) takes precedence over
  // the welcome-code cookie — don't tempt the user to overwrite it.
  const hasUrlPromotionalCode = Boolean(searchParams.get("promotionalCode"));

  const onBannerClose = useCallback(
    (variant: string) => {
      const setHostName = window.location.hostname;
      if (variant === "promo") {
        Cookies.set("yzhpb", "true", {
          domain: setHostName === "localhost" ? "localhost" : ".yardzen.com",
        });
        setHideDiscountCodeBanner(Cookies.get("yzhpb"));
        if (announcementToggle && !hideAnnouncementBanner) {
          setCurrentBannerVariant("announcement");
        } else {
          setCurrentBannerVariant("hidden");
        }
      }
      if (variant === "announcement") {
        Cookies.set("yzhab", "true", {
          domain: setHostName === "localhost" ? "localhost" : ".yardzen.com",
        });
        setHideAnnouncementBanner(Cookies.get("yzhab"));
        if (
          discountCodeCookie &&
          !hideDiscountCodeBanner &&
          !hasUrlPromotionalCode
        ) {
          setCurrentBannerVariant("promo");
        } else {
          setCurrentBannerVariant("hidden");
        }
      }
    },
    [
      announcementToggle,
      discountCodeCookie,
      hasUrlPromotionalCode,
      hideAnnouncementBanner,
      hideDiscountCodeBanner,
    ],
  );

  // Mount effect
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Cookie effect - only runs after mount
  useEffect(() => {
    if (!isMounted) return;

    setHideDiscountCodeBanner(Cookies.get("yzhpb"));
    setDiscountCodeCookie(Cookies.get("yzpdc"));
    setHideAnnouncementBanner(Cookies.get("yzhab"));

    if (
      (announcementToggle && hideAnnouncementBanner) ||
      (discountCodeCookie && hideDiscountCodeBanner)
    ) {
      setCurrentBannerVariant("hidden");
    }

    if (announcementToggle && !hideAnnouncementBanner) {
      setCurrentBannerVariant("announcement");
    }

    if (
      discountCodeCookie &&
      !hideDiscountCodeBanner &&
      !hasUrlPromotionalCode
    ) {
      setCurrentBannerVariant("promo");
    }
  }, [
    isMounted,
    announcementToggle,
    discountCodeCookie,
    hasUrlPromotionalCode,
    hideAnnouncementBanner,
    hideDiscountCodeBanner,
  ]);

  if (!contentfulBanner) {
    console.log("Failed to load announcement banner.");
    return null;
  }

  function shouldShowBanner() {
    if (
      pathname === "/" ||
      pathname === "/home" ||
      pathname === "/packages" ||
      pathname === "/how-it-works" ||
      pathname === "/full-yard" ||
      pathname === "/front-yard" ||
      pathname === "/back-yard" ||
      pathname === "/curb-appeal" ||
      pathname === "/outdoor-transformation" ||
      pathname === "/botanical" ||
      pathname === "/premium" ||
      pathname === "/design-consultation" ||
      pathname === "/gallery" ||
      pathname === "/studio-mcgee" ||
      pathname === "/checkout" ||
      pathname === "careers" ||
      pathname === "contractors" ||
      pathname === "faq" ||
      pathname === "privacy-policy" ||
      pathname === "terms-and-conditions" ||
      pathname === "outdoor-living-space-design" ||
      pathname === "/package-options" ||
      pathname === "/backyard-options" ||
      pathname === "/full-yard-options" ||
      pathname === "/front-yard-options" ||
      pathname === "/back-yard-details" ||
      pathname === "/full-yard-details" ||
      pathname === "/premium-details" ||
      pathname === "/premium-options" ||
      pathname === "/gtm-testing" ||
      pathname === "/water-smart-yard" ||
      (pathname.startsWith("/design") &&
        !pathname.startsWith("/design-project")) ||
      pathname.startsWith("/checkout") ||
      pathname.startsWith("/region") ||
      pathname.startsWith("/design-plans") ||
      pathname.startsWith("/back-yard") ||
      pathname.startsWith("/full-yard") ||
      pathname.startsWith("/landscape") ||
      pathname === "/atlanta-landscape-design" ||
      pathname === "/phoenix-landscape-design" ||
      pathname === "/los-angeles-landscape-design" ||
      pathname === "/mill-valley-landscape-design" ||
      pathname === "/arlington-landscape-design" ||
      pathname === "/sacramento-landscape-design" ||
      pathname === "/bellevue-landscape-design" ||
      pathname === "/portland-landscape-design" ||
      pathname === "/landscape-design-in-long-island" ||
      pathname === "/palm-beach-landscape-design" ||
      pathname === "/tampa-landscape-design" ||
      pathname === "/winter-park-landscape-design" ||
      pathname === "/residential-development" ||
      pathname === "/commercial" ||
      pathname === "/american-rewilding-project" ||
      pathname === "/member-pricing" ||
      pathname === "/build-with-yardzen" ||
      pathname === "/how-build-works" ||
      pathname === "/renofi" ||
      pathname === "/trend-report-2026" ||
      pathname === "/schedule-free-consult-call" ||
      pathname === "/paid-landscaping" ||
      pathname === "/crate-cb2" ||
      pathname === "/zonezero" ||
      pathname === "/toll-brothers" ||
      pathname === "/toll-brothers-packages" ||
      pathname === "/toll-brothers-redeem"
    ) {
      return true;
    } else {
      return false;
    }
  }

  return (
    shouldShowBanner() &&
    currentBannerVariant !== "hidden" && (
      <Banner
        variant={currentBannerVariant}
        promoCode={discountCodeCookie}
        bannerContent={contentfulBanner}
        onBannerClose={onBannerClose}
      />
    )
  );
}

export default AnnouncementBanner;
